import React from "react";
import { Tag, Tooltip, Dropdown, Menu, Popconfirm, Badge } from "antd";
import { Draggable } from "@hello-pangea/dnd";
import {
  GlobalOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  MessageOutlined,
  UserOutlined,
  CalendarOutlined,
  MoreOutlined,
  SwapOutlined,
  DeleteOutlined,
  LinkOutlined,
  LinkedinOutlined,
} from "@ant-design/icons";

export default function CRMCard({
  card,
  index,
  accentColor,
  currentColumn,
  allColumns,
  unreadCount,
  onClick,
  onMoveColumn,
  onDelete,
}) {
  const draggableId = String(card._id);
  const createdBy = card.createdBy?.name || "—";
  const assignedTo = card.assignedTo?.name || "—";
  const createdAt = card.createdAt
    ? new Date(card.createdAt).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

  const hasUnread = unreadCount > 0;
  const hasLink = !!card.sourceLink;
  const hasLinkedin = !!card.linkedinLink;
  const moveOptions = allColumns.filter((col) => col.key !== currentColumn.key);
  const stopProp = (e) => e.stopPropagation();

  const menu = (
    <Menu>
      <Menu.SubMenu key="move" icon={<SwapOutlined />} title="Move to">
        {moveOptions.map((col) => (
          <Menu.Item
            key={col.key}
            onClick={({ domEvent }) => {
              domEvent.stopPropagation();
              onMoveColumn(draggableId, currentColumn.key, col.key);
            }}
          >
            {col.label}
          </Menu.Item>
        ))}
      </Menu.SubMenu>
      <Menu.Divider />
      <Menu.Item
        key="delete"
        icon={<DeleteOutlined />}
        danger
        onClick={stopProp}
      >
        <Popconfirm
          title="Delete this requirement?"
          okText="Yes, delete"
          cancelText="Cancel"
          okButtonProps={{ danger: true }}
          onConfirm={() => onDelete(draggableId, currentColumn.key)}
          onClick={stopProp}
        >
          <span onClick={stopProp}>Delete</span>
        </Popconfirm>
      </Menu.Item>
    </Menu>
  );

  return (
    <Draggable draggableId={draggableId} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className="crm-card"
          style={{
            borderLeft: `4px solid ${accentColor}`,
            boxShadow: snapshot.isDragging
              ? "0 8px 24px rgba(54,153,255,0.3)"
              : hasUnread
                ? `0 0 0 2px ${accentColor}33` // soft glow when unread
                : undefined,
            opacity: snapshot.isDragging ? 0.92 : 1,
            cursor: snapshot.isDragging ? "grabbing" : "grab",
            ...provided.draggableProps.style,
          }}
          onClick={() => {
            if (!snapshot.isDragging) onClick();
          }}
        >
          {/* Top row */}
          <div className="crm-card-top">
            <span className="crm-card-job-name">{card.jobName}</span>

            <div
              className="crm-card-actions"
              onClick={stopProp}
              onMouseDown={stopProp}
            >
              {/* Message count + unread badge */}
              <Tooltip
                title={
                  hasUnread
                    ? `${unreadCount} unread message${unreadCount > 1 ? "s" : ""}`
                    : "Messages"
                }
              >
                <div
                  className="crm-card-msg-badge"
                  style={{
                    background: hasUnread ? accentColor : "#f0f7ff",
                  }}
                >
                  <MessageOutlined
                    style={{
                      fontSize: 12,
                      color: hasUnread ? "#fff" : accentColor,
                    }}
                  />
                  <span
                    className="crm-card-msg-count"
                    style={{
                      color: hasUnread ? "#fff" : accentColor,
                    }}
                  >
                    {hasUnread ? unreadCount : card.messageCount || 0}
                  </span>
                  {hasUnread && <span className="crm-card-unread-dot" />}
                </div>
              </Tooltip>

              <Dropdown
                overlay={menu}
                trigger={["click"]}
                placement="bottomRight"
              >
                <span className="crm-card-more-btn" onClick={stopProp}>
                  <MoreOutlined />
                </span>
              </Dropdown>
            </div>
          </div>

          {/* Meta */}
          <div className="crm-card-meta">
            <span className="crm-card-meta-item">
              <GlobalOutlined className="crm-card-icon" /> {card.region}
            </span>
            <span className="crm-card-meta-item">
              <DollarOutlined className="crm-card-icon" />$
              {Number(card.projectValue).toLocaleString()}
            </span>
            <span className="crm-card-meta-item">
              <ClockCircleOutlined className="crm-card-icon" /> {card.duration}{" "}
              mo
            </span>
            {card.hourlyRate && (
              <span className="crm-card-meta-item">
                <DollarOutlined className="crm-card-icon" /> ${card.hourlyRate}
                /hr
              </span>
            )}
          </div>

          {/* Source link button */}
          <div
            className="crm-card-link-row"
            onClick={stopProp}
            onMouseDown={stopProp}
          >
            <Tooltip
              title={hasLink ? "Open source link" : "No source link added"}
            >
              <a
                href={hasLink ? card.sourceLink : undefined}
                target="_blank"
                rel="noopener noreferrer"
                className={`crm-card-link-btn mr-5 ${hasLink ? "" : "crm-card-link-btn--disabled"}`}
                onClick={(e) => {
                  stopProp(e);
                  if (!hasLink) e.preventDefault();
                }}
              >
                <LinkOutlined style={{ fontSize: 11, marginRight: 4 }} />
                {hasLink ? "Source Link" : "No Link"}
              </a>
            </Tooltip>
            {hasLinkedin && (
              <Tooltip title="Open LinkedIn profile">
                <a
                  href={card.linkedinLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="crm-card-link-btn ml-5"
                  onClick={stopProp}
                >
                  <LinkedinOutlined style={{ fontSize: 11, marginRight: 4 }} />
                  LinkedIn
                </a>
              </Tooltip>
            )}
          </div>

          {/* Footer */}
          <div className="crm-card-footer">
            <div className="crm-card-people">
              <Tooltip title={`Created by: ${createdBy}`}>
                <Tag
                  icon={<UserOutlined />}
                  className="crm-card-tag crm-card-tag-created"
                >
                  {createdBy.split(" ")[0]}
                </Tag>
              </Tooltip>
              <Tooltip title={`Assigned to: ${assignedTo}`}>
                <Tag
                  icon={<UserOutlined />}
                  className="crm-card-tag crm-card-tag-assigned"
                  style={{ borderColor: accentColor, color: accentColor }}
                >
                  {assignedTo.split(" ")[0]}
                </Tag>
              </Tooltip>
            </div>
            <span className="crm-card-date">
              <CalendarOutlined style={{ marginRight: 4 }} />
              {createdAt}
            </span>
          </div>
        </div>
      )}
    </Draggable>
  );
}

// import React from "react";
// import { Tag, Tooltip, Dropdown, Menu, Popconfirm, Badge } from "antd";
// import { Draggable } from "@hello-pangea/dnd";
// import {
//   GlobalOutlined,
//   DollarOutlined,
//   ClockCircleOutlined,
//   MessageOutlined,
//   UserOutlined,
//   CalendarOutlined,
//   MoreOutlined,
//   SwapOutlined,
//   DeleteOutlined,
// } from "@ant-design/icons";

// export default function CRMCard({
//   card,
//   index,
//   accentColor,
//   currentColumn,
//   allColumns,
//   unreadCount,
//   onClick,
//   onMoveColumn,
//   onDelete,
// }) {
//   const draggableId = String(card._id);
//   const createdBy = card.createdBy?.name || "—";
//   const assignedTo = card.assignedTo?.name || "—";
//   const createdAt = card.createdAt
//     ? new Date(card.createdAt).toLocaleDateString("en-IN", {
//         day: "2-digit",
//         month: "short",
//         year: "numeric",
//       })
//     : "—";

//   const hasUnread = unreadCount > 0;
//   // TEMP — remove after confirming
//   if (hasUnread)
//     console.log("[card] unread badge for", card.jobName, "=", unreadCount);
//   const moveOptions = allColumns.filter((col) => col.key !== currentColumn.key);
//   const stopProp = (e) => e.stopPropagation();

//   const menu = (
//     <Menu>
//       <Menu.SubMenu key="move" icon={<SwapOutlined />} title="Move to">
//         {moveOptions.map((col) => (
//           <Menu.Item
//             key={col.key}
//             onClick={({ domEvent }) => {
//               domEvent.stopPropagation();
//               onMoveColumn(draggableId, currentColumn.key, col.key);
//             }}
//           >
//             {col.label}
//           </Menu.Item>
//         ))}
//       </Menu.SubMenu>
//       <Menu.Divider />
//       <Menu.Item
//         key="delete"
//         icon={<DeleteOutlined />}
//         danger
//         onClick={stopProp}
//       >
//         <Popconfirm
//           title="Delete this requirement?"
//           okText="Yes, delete"
//           cancelText="Cancel"
//           okButtonProps={{ danger: true }}
//           onConfirm={() => onDelete(draggableId, currentColumn.key)}
//           onClick={stopProp}
//         >
//           <span onClick={stopProp}>Delete</span>
//         </Popconfirm>
//       </Menu.Item>
//     </Menu>
//   );

//   return (
//     <Draggable draggableId={draggableId} index={index}>
//       {(provided, snapshot) => (
//         <div
//           ref={provided.innerRef}
//           {...provided.draggableProps}
//           {...provided.dragHandleProps}
//           className="crm-card"
//           style={{
//             borderLeft: `4px solid ${accentColor}`,
//             boxShadow: snapshot.isDragging
//               ? "0 8px 24px rgba(54,153,255,0.3)"
//               : hasUnread
//                 ? `0 0 0 2px ${accentColor}33` // soft glow when unread
//                 : undefined,
//             opacity: snapshot.isDragging ? 0.92 : 1,
//             cursor: snapshot.isDragging ? "grabbing" : "grab",
//             ...provided.draggableProps.style,
//           }}
//           onClick={() => {
//             if (!snapshot.isDragging) onClick();
//           }}
//         >
//           {/* Top row */}
//           <div className="crm-card-top">
//             <span className="crm-card-job-name">{card.jobName}</span>

//             <div
//               className="crm-card-actions"
//               onClick={stopProp}
//               onMouseDown={stopProp}
//             >
//               {/* Message count + unread badge */}
//               <Tooltip
//                 title={
//                   hasUnread
//                     ? `${unreadCount} unread message${unreadCount > 1 ? "s" : ""}`
//                     : "Messages"
//                 }
//               >
//                 <div
//                   className="crm-card-msg-badge"
//                   style={{
//                     background: hasUnread ? accentColor : "#f0f7ff",
//                   }}
//                 >
//                   <MessageOutlined
//                     style={{
//                       fontSize: 12,
//                       color: hasUnread ? "#fff" : accentColor,
//                     }}
//                   />
//                   <span
//                     className="crm-card-msg-count"
//                     style={{
//                       color: hasUnread ? "#fff" : accentColor,
//                     }}
//                   >
//                     {hasUnread ? unreadCount : card.messageCount || 0}
//                   </span>
//                   {hasUnread && <span className="crm-card-unread-dot" />}
//                 </div>
//               </Tooltip>

//               <Dropdown
//                 overlay={menu}
//                 trigger={["click"]}
//                 placement="bottomRight"
//               >
//                 <span className="crm-card-more-btn" onClick={stopProp}>
//                   <MoreOutlined />
//                 </span>
//               </Dropdown>
//             </div>
//           </div>

//           {/* Meta */}
//           <div className="crm-card-meta">
//             <span className="crm-card-meta-item">
//               <GlobalOutlined className="crm-card-icon" /> {card.region}
//             </span>
//             <span className="crm-card-meta-item">
//               <DollarOutlined className="crm-card-icon" />$
//               {Number(card.projectValue).toLocaleString()}
//             </span>
//             <span className="crm-card-meta-item">
//               <ClockCircleOutlined className="crm-card-icon" /> {card.duration}
//             </span>
//             {card.hourlyRate && (
//               <span className="crm-card-meta-item">
//                 <DollarOutlined className="crm-card-icon" /> ${card.hourlyRate}
//                 /hr
//               </span>
//             )}
//           </div>

//           {/* Footer */}
//           <div className="crm-card-footer">
//             <div className="crm-card-people">
//               <Tooltip title={`Created by: ${createdBy}`}>
//                 <Tag
//                   icon={<UserOutlined />}
//                   className="crm-card-tag crm-card-tag-created"
//                 >
//                   {createdBy.split(" ")[0]}
//                 </Tag>
//               </Tooltip>
//               <Tooltip title={`Assigned to: ${assignedTo}`}>
//                 <Tag
//                   icon={<UserOutlined />}
//                   className="crm-card-tag crm-card-tag-assigned"
//                   style={{ borderColor: accentColor, color: accentColor }}
//                 >
//                   {assignedTo.split(" ")[0]}
//                 </Tag>
//               </Tooltip>
//             </div>
//             <span className="crm-card-date">
//               <CalendarOutlined style={{ marginRight: 4 }} />
//               {createdAt}
//             </span>
//           </div>
//         </div>
//       )}
//     </Draggable>
//   );
// }
